import { HTMLAttributes, forwardRef, ReactNode } from 'react';
import { Label } from './Label';
import './Form.css';

export interface FormFieldProps extends HTMLAttributes<HTMLDivElement> {
  /** Label text */
  label?: ReactNode;
  /** Field is required */
  required?: boolean;
  /** Error message */
  error?: string;
  /** Help text */
  hint?: string;
  /** ID for the form control (used for htmlFor) */
  htmlFor?: string;
}

/**
 * FormField component that wraps form controls with label, hint, and error.
 *
 * @example
 * ```tsx
 * <FormField label="Email" required error={errors.email} htmlFor="email">
 *   <Input id="email" type="email" error={!!errors.email} />
 * </FormField>
 * ```
 */
export const FormField = forwardRef<HTMLDivElement, FormFieldProps>(
  (
    {
      label,
      required = false,
      error,
      hint,
      htmlFor,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const classNames = [
      'ui-form-field',
      error && 'ui-form-field--error',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div ref={ref} className={classNames} {...props}>
        {label && (
          <Label htmlFor={htmlFor} required={required}>
            {label}
          </Label>
        )}
        {children}
        {hint && !error && <p className="ui-form-field__hint">{hint}</p>}
        {error && <p className="ui-form-field__error">{error}</p>}
      </div>
    );
  }
);

FormField.displayName = 'FormField';
